import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { CameraMemo } from "~/entities/camera-memo/types";
import { type CameraMemoAction, updateCameraMemos } from "../api/update-camera-memos";
import { CAMERA_MEMOS_QUERY_KEY } from "./use-camera-memos";

/**
 * 낙관적으로 추가된(=아직 서버 id 를 못 받은) 메모의 임시 id.
 * 실제 id 와 절대 겹치지 않도록 음수를 쓴다. 이 id 를 가진 메모는
 * 서버에 아직 없으므로 update/delete 대상으로 삼으면 안 된다 (isPendingMemo 참고).
 */
let tempMemoSeq = -1;
function nextTempMemoId() {
  return tempMemoSeq--;
}

/** 아직 서버 저장이 끝나지 않아 실제 id 가 없는 메모인지 */
export function isPendingMemo(memo: Pick<CameraMemo, "id">) {
  return memo.id < 0;
}

/** 낙관적 업데이트 — 서버 응답을 기다리지 않고 캐시에 액션을 미리 반영한다. */
function applyActions(memos: CameraMemo[], actions: CameraMemoAction[]): CameraMemo[] {
  let next = memos;

  for (const action of actions) {
    if (action.actionType === "create") {
      next = [...next, { ...action.data, id: nextTempMemoId(), updatedAt: Date.now() / 1000 }];
    } else if (action.actionType === "update") {
      next = next.map((m) => (m.id === action.data.id ? { ...action.data } : m));
    } else {
      next = next.filter((m) => m.id !== action.data.id);
    }
  }

  return next;
}

export function useUpdateCameraMemos(branchUid: string, versionId?: number) {
  const queryClient = useQueryClient();
  const queryKey = [CAMERA_MEMOS_QUERY_KEY, branchUid, versionId];

  return useMutation({
    mutationFn: (actions: CameraMemoAction[]) =>
      updateCameraMemos(branchUid, versionId, actions),
    onMutate: async (actions) => {
      // 진행 중인 refetch 가 낙관적 결과를 덮어쓰지 않도록 먼저 취소
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<CameraMemo[]>(queryKey);
      if (previous) {
        queryClient.setQueryData<CameraMemo[]>(queryKey, applyActions(previous, actions));
      }
      return { previous };
    },
    onError: (_error, _actions, context) => {
      // 실패하면 낙관적 반영을 되돌린다 (화면이 서버 상태와 어긋난 채 남지 않도록)
      if (context?.previous) {
        queryClient.setQueryData<CameraMemo[]>(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
